/**
 * Admin → user journey drawer.
 *
 * Single-page summary of one customer's lifecycle on Drone Maps:
 *   • Header — name, email, country, current funnel step, subscription tag.
 *   • Cards — drones owned, missions created, PDFs exported, reminders + opt-out.
 *   • Mission list with compliance/risk scores — clickable to inspect the
 *     polygon geometry & full regulation_result of any individual mission.
 *   • Reverse-chronological timeline merging every dated event we know of.
 *
 * Data source: GET /api/admin/users/{user_id}/journey — server-side
 * aggregator that returns everything in one round-trip.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  X, MapPin, Plane, FileText, Mail, UserMinus, ShieldCheck,
  AlertTriangle, CalendarDays, LogIn, UserPlus, Send, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_LABEL = {
  signed_up:       'Inscrit',
  logged_in:       'Connecté',
  drone_added:     'Drone ajouté',
  mission_created: 'Mission créée',
  pdf_exported:    'PDF exporté',
};

const STEP_TINT = {
  signed_up:       'bg-slate-100 text-slate-600',
  logged_in:       'bg-amber-100 text-amber-700',
  drone_added:     'bg-blue-100 text-blue-700',
  mission_created: 'bg-emerald-100 text-emerald-700',
  pdf_exported:    'bg-violet-100 text-violet-700',
};

const EVENT_ICON = {
  signup:      UserPlus,
  login:       LogIn,
  drone:       Plane,
  mission:     MapPin,
  pdf:         FileText,
  reminder:    Send,
  unsubscribe: UserMinus,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtShortDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

export function AdminUserJourneyDrawer({ userId, onClose, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMissionId, setOpenMissionId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/users/${userId}/journey`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, token]);

  if (!userId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex"
      data-testid="admin-user-journey-drawer"
      onClick={onClose}
    >
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" />
      <div
        className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 font-[Barlow]">Parcours utilisateur</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-md"
            data-testid="journey-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {loading && <div className="p-10 text-center text-slate-500">Chargement…</div>}
        {error && <div className="m-6 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">Erreur : {error}</div>}

        {data && (
          <div className="p-6 space-y-6">
            <UserHeader user={data.user} step={data.step} subscription={data.subscription} />
            <CountsCards data={data} />
            <DronesSection drones={data.drones} />
            <MissionsSection
              missions={data.missions}
              openId={openMissionId}
              onToggle={(id) => setOpenMissionId(openMissionId === id ? null : id)}
            />
            <TimelineSection timeline={data.timeline} />
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Sub-components ------------------------------------------------------

const UserHeader = ({ user, step, subscription }) => (
  <div className="border border-slate-200 rounded-xl p-5 bg-gradient-to-br from-slate-50 to-white">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-xl font-semibold text-slate-900 truncate">{user.name || user.email.split('@')[0]}</div>
        <div className="text-sm text-slate-500 font-mono truncate">{user.email}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Inscrit le {fmtShortDate(user.created_at)}</span>
          {user.country && <span className="text-slate-400">·</span>}
          {user.country && <span className="text-slate-500">Pays : <strong className="uppercase font-mono">{user.country}</strong></span>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STEP_TINT[step] || STEP_TINT.signed_up}`}>
          {STEP_LABEL[step] || step}
        </span>
        {subscription?.status && (
          <span className={`text-xs px-2.5 py-1 rounded-full border ${subscription.status === 'active' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-50 text-slate-600'}`}>
            {subscription.status === 'active' ? 'Abonnement actif' : `Abonnement: ${subscription.status}`}
            {subscription.tier ? ` · ${subscription.tier}` : ''}
          </span>
        )}
        {user.unsubscribed_at && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 inline-flex items-center gap-1">
            <UserMinus className="w-3 h-3" /> Désabonné le {fmtShortDate(user.unsubscribed_at)}
          </span>
        )}
      </div>
    </div>
  </div>
);

const CountsCards = ({ data }) => {
  const pdfCount = data.missions.filter((m) => m.pdf_exported_at).length;
  const remindersSent = Object.values(data.reminders_sent || {}).filter((v) => v && v !== 'SKIPPED_HAS_MISSION').length;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card icon={Plane}     label="Drones"   value={data.drones.length} />
      <Card icon={MapPin}    label="Missions" value={data.missions.length} />
      <Card icon={FileText}  label="PDF exportés" value={pdfCount} />
      <Card icon={Mail}      label="Relances reçues" value={remindersSent} />
    </div>
  );
};

const Card = ({ icon: Icon, label, value }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white">
    <div className="flex items-center justify-between text-xs uppercase text-slate-500 font-medium mb-1">
      <span>{label}</span>
      <Icon className="w-3.5 h-3.5 text-slate-400" />
    </div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
  </div>
);

const DronesSection = ({ drones }) => {
  if (!drones.length) return null;
  return (
    <section data-testid="journey-drones">
      <SectionTitle icon={Plane} label="Drones" />
      <div className="space-y-2">
        {drones.map((d) => (
          <div key={d.id} className="border border-slate-200 rounded-md px-3 py-2 text-sm flex items-center justify-between">
            <div>
              <span className="font-medium text-slate-800">{d.model_name || '—'}</span>
              <span className="text-slate-500 ml-2">· {d.weight_kg} kg · CE {d.ce_class || '—'}</span>
            </div>
            <span className="text-xs text-slate-400 font-mono">{d.serial_number || ''}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const MissionsSection = ({ missions, openId, onToggle }) => {
  if (!missions.length) {
    return (
      <section data-testid="journey-missions">
        <SectionTitle icon={MapPin} label="Missions" />
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-4 text-center">
          Aucune mission créée pour cet utilisateur.
        </div>
      </section>
    );
  }
  return (
    <section data-testid="journey-missions">
      <SectionTitle icon={MapPin} label={`Missions (${missions.length})`} />
      <div className="space-y-2">
        {missions.map((m) => (
          <MissionRow key={m.id} mission={m} expanded={openId === m.id} onToggle={() => onToggle(m.id)} />
        ))}
      </div>
    </section>
  );
};

const MissionRow = ({ mission, expanded, onToggle }) => {
  const score = mission.compliance_score ?? mission.risk_score;
  // 0-100 risk score: <30 green, <60 amber, else rose
  let scoreTint = 'bg-slate-100 text-slate-600';
  if (score != null) {
    if (score < 30)      scoreTint = 'bg-emerald-100 text-emerald-700';
    else if (score < 60) scoreTint = 'bg-amber-100 text-amber-700';
    else                 scoreTint = 'bg-rose-100 text-rose-700';
  }
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={`journey-mission-row-${mission.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-800 truncate">
              Mission {mission.mission_type ? `· ${mission.mission_type}` : ''} {mission.is_night_mission ? '🌙' : ''} {mission.is_urban_zone ? '🏙️' : ''}
            </div>
            <div className="text-xs text-slate-500">
              {fmtDate(mission.created_at)} · Alt. {mission.altitude_requested ?? '—'} m
              {mission.pdf_exported_at && ' · 📄 PDF'}
            </div>
          </div>
        </div>
        {score != null && (
          <span className={`text-xs px-2 py-0.5 rounded font-mono shrink-0 ${scoreTint}`}>
            score {score}
          </span>
        )}
      </button>
      {expanded && <MissionDetail mission={mission} />}
    </div>
  );
};

const MissionDetail = ({ mission }) => {
  const reg = mission.regulation_result || {};
  const restrictions = reg.restrictions || reg.zones_detected || reg.applicable_restrictions || [];
  const geom = mission.polygon_geometry;
  const coords = geom?.coordinates?.[0] || [];
  return (
    <div className="border-t border-slate-100 bg-slate-50/40 p-3 space-y-3 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Field label="Date de vol" value={mission.mission_date ? `${mission.mission_date} ${mission.regulation_result?.time || ''}` : '—'} />
        <Field label="Verdict" value={mission.verdict || '—'} icon={mission.verdict === 'compliant' ? ShieldCheck : AlertTriangle} />
        <Field label="Status DB" value={mission.status || '—'} />
        <Field label="Geometry" value={geom ? `${geom.type} · ${coords.length} pts` : '—'} />
      </div>

      {Array.isArray(restrictions) && restrictions.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-1.5">Restrictions détectées ({restrictions.length})</div>
          <ul className="space-y-1 text-xs">
            {restrictions.slice(0, 8).map((r, i) => (
              <li key={i} className="border border-slate-200 rounded px-2 py-1 bg-white flex items-center justify-between gap-2">
                <span className="text-slate-700 truncate">
                  {r.name || r.zone_name || r.label || r.type || JSON.stringify(r).slice(0, 60)}
                </span>
                {r.type && <span className="font-mono text-[10px] text-slate-400 shrink-0">{r.type}</span>}
              </li>
            ))}
            {restrictions.length > 8 && (
              <li className="text-xs text-slate-400 italic">… et {restrictions.length - 8} de plus</li>
            )}
          </ul>
        </div>
      )}

      {coords.length > 0 && <MissionMiniMap geometry={geom} />}
    </div>
  );
};

/**
 * Mission mini-map — renders the polygon the user drew on a small OSM
 * basemap so the admin can spot-check the area visually. Lazy: only
 * instantiated when the parent expansion is mounted.
 */
const MissionMiniMap = ({ geometry }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, {
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
    }).setView([46.5, 2.3], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      try { map.remove(); } catch { /* already removed */ }
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geometry) return;
    let layer;
    try {
      layer = L.geoJSON(geometry, {
        style: { color: '#dc2626', weight: 2, fillColor: '#dc2626', fillOpacity: 0.18 },
      }).addTo(map);
      const bounds = layer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [16, 16], maxZoom: 15 });
      // Leaflet sometimes needs an explicit size recompute when injected
      // inside a freshly-expanded <div> (which had no width at mount).
      setTimeout(() => map.invalidateSize(), 80);
    } catch (err) {
      console.warn('[MissionMiniMap] failed to render geometry', err);
    }
    return () => {
      if (layer && map.hasLayer(layer)) map.removeLayer(layer);
    };
  }, [geometry]);

  return (
    <div className="mt-2 border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid="mission-mini-map">
      <div ref={ref} style={{ width: '100%', height: 220 }} />
      <div className="flex items-center justify-between px-2 py-1 text-[11px] text-slate-400 border-t border-slate-200 bg-white">
        <span>© OpenStreetMap contributors</span>
        <span className="font-mono">{geometry?.type} · {geometry?.coordinates?.[0]?.length || 0} pts</span>
      </div>
    </div>
  );
};

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-[10px] uppercase font-semibold text-slate-500">{label}</div>
    <div className="text-sm text-slate-800 inline-flex items-center gap-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      {value}
    </div>
  </div>
);

const TimelineSection = ({ timeline }) => {
  if (!timeline?.length) return null;
  return (
    <section data-testid="journey-timeline">
      <SectionTitle icon={CalendarDays} label={`Timeline (${timeline.length})`} />
      <ol className="relative border-l-2 border-slate-100 ml-2 pl-4 space-y-3">
        {timeline.map((ev, i) => {
          const Icon = EVENT_ICON[ev.kind] || CalendarDays;
          return (
            <li key={i} className="relative">
              <div className="absolute -left-[26px] top-0.5 w-5 h-5 rounded-full bg-white border-2 border-sky-200 flex items-center justify-center">
                <Icon className="w-2.5 h-2.5 text-sky-600" />
              </div>
              <div className="text-xs text-slate-400 mb-0.5">{fmtDate(ev.ts)}</div>
              <div className="text-sm text-slate-800">{ev.label}</div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const SectionTitle = ({ icon: Icon, label }) => (
  <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">
    <Icon className="w-4 h-4 text-sky-600" />
    {label}
  </h3>
);
